import { FlexRowBetween, LoaderBar, PageHeader } from 'components'
import { Form, Modal, Radio } from 'semantic-ui-react'
import { GetClaimList, GetUserList } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { X } from 'phosphor-react'
import {
    AppPrimaryButton,
    FormDateField,
    FormCheckBoxField,
    FormDropdownField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import { CustomerDefectReportList } from 'page-components'
import {
    AuthService,
    ClaimListService,
    DatePickerTypes,
} from 'utility'
import styled from 'styled-components'
import {
    buildCustomerExtensiveDropdownOptions,
    CUSTOMER_TYPE_RADIO_OPTIONS,
    filterSupplierDropdownOptions,
    filterSupplierOptionsByClientType,
    getClaimAuditId,
    getClaimDisplayName,
    getClaimRowId,
    SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
} from 'page-components/customer-complaint-log/customer-claim-inspection-utils'

const CustomerTypeRadioGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.35rem;
`

const ClaimShowChecks = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.75rem;
    flex-wrap: nowrap;
    margin-top: 0.55rem;

    .field {
        width: auto !important;
        margin: 0 !important;
        flex: 0 0 auto;
    }
`

const PHASE_FILTER_OPTIONS = [
    { key: 'any', value: '', text: 'Any phase' },
    { key: 'p1', value: '1', text: 'Created' },
    { key: 'p2', value: '2', text: 'Waiting on Quality' },
    { key: 'p3', value: '3', text: 'Completed' },
]

const STATUS_FILTER_OPTIONS = [
    { key: 'any', value: '', text: 'Any status' },
    { key: 'open', value: '2', text: 'In progress' },
    { key: 'closed', value: '5', text: 'Closed' },
]

const formatApiDate = value => {
    if (!value) return ''
    try {
        const date = value instanceof Date ? value : new Date(value)
        if (Number.isNaN(date.getTime())) return ''
        const pad = n => String(n).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    } catch {
        return ''
    }
}

export const CustomerDefectReportScreen = () => {
    const {
        data,
        loading,
        filterChanged,
        filter,
        totalCount,
        pageChanged,
        setData,
        refresh,
    } = GetClaimList(SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID)

    const [customerType, setCustomerType] = useState('C')
    const [selectedClaimIds, setSelectedClaimIds] = useState([])
    const [selectedClaimsById, setSelectedClaimsById] = useState({})
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
    const [processing, setProcessing] = useState('')
    const [customerOptions, setCustomerOptions] = useState([])
    const [customersLoading, setCustomersLoading] = useState(false)

    const { control, handleSubmit, reset, watch, setValue } = useForm({
        defaultValues: {
            q: '',
            phase: '',
            status: '',
            startDate: null,
            endDate: null,
            customerId: '',
            originatorUserId: '',
            showRMA: true,
            showNonRMA: true,
        },
    })

    const startDateValue = watch('startDate')
    const { options: userOptions, loading: usersLoading } = GetUserList()
    const originatorOptions = useMemo(
        () => [
            { key: 'any', value: '', text: 'All originators' },
            ...(userOptions || []).map(option => ({
                key: option.value,
                value: option.value,
                text: option.text,
            })),
        ],
        [userOptions],
    )

    const companyId =
        AuthService.getUser()?.CompanyId ||
        AuthService.getUser()?.Companies?.[0]?.CompanyId

    useEffect(() => {
        let mounted = true
        const loadCustomers = async () => {
            try {
                setCustomersLoading(true)
                const rows = await ClaimListService.getCustomerExtensive({
                    q: '',
                    companyId: companyId || '',
                    onTemplateId: SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
                    limit: 500,
                })
                if (!mounted) return
                const list = Array.isArray(rows)
                    ? rows
                    : rows?.Items || rows?.items || rows?.Data || []
                setCustomerOptions(buildCustomerExtensiveDropdownOptions(list))
            } catch {
                if (mounted) setCustomerOptions([])
            } finally {
                if (mounted) setCustomersLoading(false)
            }
        }
        loadCustomers()
        return () => {
            mounted = false
        }
    }, [companyId])

    const filteredCustomerOptions = useMemo(
        () =>
            filterSupplierOptionsByClientType(customerOptions, customerType),
        [customerOptions, customerType],
    )

    const submit = formData => {
        filterChanged({
            q: String(formData.q || '').trim(),
            phase: formData.phase || '',
            status: formData.status || '',
            startDate: formatApiDate(formData.startDate),
            endDate: formatApiDate(formData.endDate),
            customerIds: formData.customerId || '',
            OriginatorUserId: formData.originatorUserId || '',
            RAM: formData.showRMA !== false,
            NonRAM: formData.showNonRMA !== false,
            pageNumber: 1,
        })
    }

    const handleSelectionChange = ids => {
        setSelectedClaimIds(ids)
        setSelectedClaimsById(prev => {
            const next = {}
            ids.forEach(id => {
                const fromPage = (data || []).find(
                    item => getClaimRowId(item) === id,
                )
                if (fromPage) {
                    next[id] = fromPage
                } else if (prev[id]) {
                    next[id] = prev[id]
                }
            })
            return next
        })
    }

    const resetForm = () => {
        setCustomerType('C')
        setSelectedClaimIds([])
        setSelectedClaimsById({})
        reset({
            q: '',
            phase: '',
            status: '',
            startDate: null,
            endDate: null,
            customerId: '',
            originatorUserId: '',
            showRMA: true,
            showNonRMA: true,
        })
        setData([])
        filterChanged({
            q: '',
            phase: '',
            status: '',
            startDate: '',
            endDate: '',
            customerIds: '',
            OriginatorUserId: '',
            RAM: true,
            NonRAM: true,
            pageNumber: 1,
        })
    }

    const selectedClaims = selectedClaimIds
        .map(id => selectedClaimsById[id])
        .filter(Boolean)

    const deleteClaims = async items => {
        const auditIds = items.map(getClaimAuditId).filter(Boolean)
        if (!auditIds.length) {
            throw new Error('No valid AuditId selected for delete.')
        }
        await ClaimListService.bulkRemove(auditIds)
    }

    const confirmBulkDelete = async () => {
        if (!selectedClaims.length) {
            setIsBulkDeleteModalOpen(false)
            return
        }
        setIsBulkDeleteModalOpen(false)
        setProcessing('Deleting')
        try {
            await deleteClaims(selectedClaims)
            const deletedIds = new Set(selectedClaims.map(getClaimRowId))
            setData(prev =>
                (prev || []).filter(
                    item => !deletedIds.has(getClaimRowId(item)),
                ),
            )
            setSelectedClaimIds([])
            setSelectedClaimsById({})
            refresh()
        } catch (error) {
            console.error('Error deleting claims:', error)
        } finally {
            setProcessing('')
        }
    }

    return (
        <>
            <div className="">
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                <PageHeader header="All Claims" />
                <Form noValidate>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Customer Type</label>
                            <CustomerTypeRadioGroup>
                                {CUSTOMER_TYPE_RADIO_OPTIONS.map(option => (
                                    <Radio
                                        key={option.value}
                                        label={option.label}
                                        name="claimCustomerType"
                                        value={option.value}
                                        checked={customerType === option.value}
                                        onChange={() => {
                                            setCustomerType(option.value)
                                            setValue('customerId', '')
                                        }}
                                    />
                                ))}
                            </CustomerTypeRadioGroup>
                        </Form.Field>
                        <FormDropdownField
                            key={`claim-customer-${customerType}`}
                            control={control}
                            name="customerId"
                            label="Customer"
                            placeholder="All customers"
                            options={filteredCustomerOptions}
                            search={filterSupplierDropdownOptions}
                            clearable
                            disabled={customersLoading}
                        />
                        <FormTextFormField
                            control={control}
                            name="q"
                            label="Search"
                            placeholder="Search by Claim # or Audit Name"
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDateField
                            control={control}
                            name="startDate"
                            label="Start Date"
                            type={DatePickerTypes.Single}
                        />
                        <FormDateField
                            control={control}
                            name="endDate"
                            label="End Date"
                            disabledBefore={startDateValue}
                            type={DatePickerTypes.Single}
                        />
                        <FormDropdownField
                            control={control}
                            name="phase"
                            label="Phase"
                            placeholder="Any phase"
                            options={PHASE_FILTER_OPTIONS}
                            defaultValue=""
                            clearable
                        />
                        <FormDropdownField
                            control={control}
                            name="status"
                            label="Status"
                            placeholder="Any status"
                            options={STATUS_FILTER_OPTIONS}
                            defaultValue=""
                            clearable
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="originatorUserId"
                            label="Originator"
                            placeholder="All originators"
                            options={originatorOptions}
                            defaultValue=""
                            clearable
                            disabled={usersLoading}
                        />
                        <Form.Field>
                            <label>&nbsp;</label>
                            <ClaimShowChecks>
                                <FormCheckBoxField
                                    control={control}
                                    name="showRMA"
                                    label="Show RMA"
                                    defaultValue
                                />
                                <FormCheckBoxField
                                    control={control}
                                    name="showNonRMA"
                                    label="Show Non RMA"
                                    defaultValue
                                />
                            </ClaimShowChecks>
                        </Form.Field>
                    </Form.Group>
                    <div className="pt-1">
                        <SubmitCancelButtons
                            onSubmit={handleSubmit(submit)}
                            onCancel={resetForm}
                        />
                    </div>
                </Form>
            </div>
            <div className="my-3">
                <CustomerDefectReportList
                    data={data}
                    loading={loading}
                    filter={filter}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    selectedIds={selectedClaimIds}
                    onSelectionChange={handleSelectionChange}
                    onBulkDelete={() => setIsBulkDeleteModalOpen(true)}
                />
            </div>
            <Modal
                open={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                size="small"
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Confirm Bulk Deletion</span>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setIsBulkDeleteModalOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling>
                    <p>
                        Are you sure you want to delete the following{' '}
                        {selectedClaims.length === 1 ? 'claim' : 'claims'}?
                    </p>
                    <div
                        style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #eee',
                            padding: '10px',
                            borderRadius: '4px',
                        }}
                    >
                        <ul>
                            {selectedClaims.map(item => (
                                <li
                                    key={getClaimRowId(item)}
                                    style={{ padding: '4px 0' }}
                                >
                                    {getClaimDisplayName(item)}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <AppPrimaryButton
                        onClick={() => setIsBulkDeleteModalOpen(false)}
                    >
                        Cancel
                    </AppPrimaryButton>
                    <AppPrimaryButton
                        onClick={confirmBulkDelete}
                        style={{ backgroundColor: '#dc3545' }}
                    >
                        Confirm Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}
